import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { EmptyChart } from './empty-chart';
import './bar-chart.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface IBarChartDataSet {
  label: string;
  data: number[];
  backgroundColor: string;
}

export interface ILineDataSet {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor?: string;
  suffix?: string; // axis tick suffix: "%", "x", "" (default: "")
}

interface IBarChartProps {
  datasets?: IBarChartDataSet[];
  isLoading?: boolean;
  labels?: string[];
  replyRate?: number[];
  lineDataset?: ILineDataSet;
}

const getThemeColors = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    grid: style.getPropertyValue('--divider-color').trim() || 'rgba(255, 255, 255, 0.06)',
    text: style.getPropertyValue('--text-secondary').trim() || '#929EAE',
    textPrimary: style.getPropertyValue('--text-primary').trim() || '#FFFFFF',
    tooltipBg: style.getPropertyValue('--bg-secondary').trim() || '#1C1C1E',
    tooltipBorder: style.getPropertyValue('--border-color').trim() || '#2C2C30',
  };
};

const formatDateLabel = (label: string) => {
  if (!label) return label;
  const parts = label.split('-');
  if (parts.length !== 3) return label;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(parts[1], 10) - 1]} ${parseInt(parts[2], 10)}`;
};

export const BarChart = ({
  isLoading = false,
  labels = [],
  datasets = [],
  replyRate = [],
  lineDataset,
}: IBarChartProps): JSX.Element => {
  const chartRef = useRef<any>(null);

  // Resolve line dataset: explicit lineDataset prop takes priority, fall back to legacy replyRate
  const resolvedLine: ILineDataSet | undefined = lineDataset ?? (replyRate?.length
    ? { label: 'Reply Rate', data: replyRate, borderColor: '#22C55E', suffix: '%' }
    : undefined);

  // Filter out days where everything is zero
  const indices: number[] = [];
  for (let i = 0; i < labels.length; i++) {
    if (datasets.some(d => (d.data?.[i] || 0) !== 0) || (resolvedLine?.data?.[i] || 0) !== 0) {
      indices.push(i);
    }
  }

  const fLabels = indices.map(i => labels[i]);
  const fDatasets = datasets.map(d => ({
    ...d,
    data: indices.map(i => d.data?.[i] || 0),
  }));
  const fRate = resolvedLine ? indices.map(i => resolvedLine.data[i] || 0) : [];
  const hasRate = fRate.some(v => v > 0);
  const pointCount = fLabels.length;

  // Check if there's any data at all
  const hasData = fLabels.length > 0;

  if (isLoading) {
    return (
      <div className="bar-chart-container">
        <div className="bar-chart-shimmer">
          <div className="bar-chart-shimmer__y-axis">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="bar-chart-shimmer__y-label" />)}
          </div>
          <div className="bar-chart-shimmer__area">
            <div className="bar-chart-shimmer__grid">
              {[0, 1, 2, 3, 4].map(i => <div key={i} className="bar-chart-shimmer__grid-line" />)}
            </div>
            <div className="bar-chart-shimmer__bars">
              {[85, 45, 70, 30, 90, 55, 40, 75, 60, 35, 80, 50].map((h, i) => (
                <div key={i} className="bar-chart-shimmer__bar-group">
                  <div className="bar-chart-shimmer__bar" style={{ height: `${h}%` }} />
                  <div className="bar-chart-shimmer__bar" style={{ height: `${h * 0.5}%` }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return <EmptyChart />;
  }

  const themeColors = getThemeColors();

  // Y-axis max for bars
  const allBarVals = fDatasets.flatMap(d => d.data);
  const barMax = Math.ceil(Math.max(...allBarVals, 0) * 1.2) || 5;
  const rateMax = hasRate ? Math.ceil(Math.max(...fRate, 0) * 1.3) || 10 : 10;
  const barRadius = pointCount > 15 ? 3 : 6;

  const data = {
    labels: fLabels,
    datasets: [
      ...fDatasets.map((ds, i) => ({
        type: 'bar' as const,
        label: ds.label,
        data: ds.data,
        backgroundColor: ds.backgroundColor,
        hoverBackgroundColor: ds.backgroundColor + 'B3',
        borderWidth: 0,
        borderRadius: barRadius,
        borderSkipped: false as const,
        yAxisID: 'y',
        order: 2,
      })),
      ...(hasRate && resolvedLine ? [{
        type: 'line' as const,
        label: resolvedLine.label,
        data: fRate,
        borderColor: resolvedLine.borderColor,
        backgroundColor: resolvedLine.backgroundColor || (resolvedLine.borderColor + '26'),
        borderWidth: 2,
        pointRadius: pointCount > 20 ? 0 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: resolvedLine.borderColor,
        pointBorderColor: resolvedLine.borderColor,
        tension: 0.35,
        fill: true,
        yAxisID: 'yRate',
        order: 1,
      }] : []),
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    layout: { padding: { top: 10, bottom: 0, left: 0, right: 0 } },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        max: barMax,
        beginAtZero: true,
        grid: { color: themeColors.grid, drawBorder: false },
        border: { display: false },
        ticks: {
          padding: 12,
          color: themeColors.text,
          font: { family: "'Kumbh Sans', sans-serif", size: 12, weight: '500' },
          stepSize: barMax <= 10 ? 1 : undefined,
        },
      },
      ...(hasRate && resolvedLine ? {
        yRate: {
          type: 'linear',
          position: 'right',
          max: rateMax,
          beginAtZero: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            padding: 12,
            color: resolvedLine.borderColor,
            font: { family: "'Kumbh Sans', sans-serif", size: 12, weight: '500' },
            callback: (v: number) => resolvedLine.suffix ? `${v}${resolvedLine.suffix}` : `${v}`,
          },
        },
      } : {}),
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: themeColors.text,
          font: { family: "'Kumbh Sans', sans-serif", size: 11, weight: '500' },
          maxRotation: 0,
          autoSkip: true,
          autoSkipPadding: 16,
          callback: (_v: any, i: number) => formatDateLabel(fLabels[i]),
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          color: themeColors.text,
          font: { family: "'Kumbh Sans', sans-serif", size: 12, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      title: { display: false },
      tooltip: {
        backgroundColor: themeColors.tooltipBg,
        borderColor: themeColors.tooltipBorder,
        borderWidth: 1,
        cornerRadius: 10,
        padding: 12,
        titleColor: themeColors.textPrimary,
        titleFont: { family: "'Kumbh Sans', sans-serif", size: 13, weight: '600' },
        bodyColor: themeColors.text,
        bodyFont: { family: "'Kumbh Sans', sans-serif", size: 12, weight: '400' },
        bodySpacing: 6,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return '';
            return formatDateLabel(items[0].label);
          },
          label: (ctx: any) => {
            const val = ctx.parsed.y;
            if (ctx.dataset.yAxisID === 'yRate') {
              const suffix = resolvedLine?.suffix || '';
              return ` ${ctx.dataset.label}: ${val.toFixed(1)}${suffix}`;
            }
            return ` ${ctx.dataset.label}: ${val}`;
          },
          labelColor: (ctx: any) => ({
            borderColor: ctx.dataset.borderColor || ctx.dataset.backgroundColor,
            backgroundColor: ctx.dataset.borderColor || ctx.dataset.backgroundColor,
            borderWidth: 0,
            borderRadius: 4,
          }),
        },
      },
    },
    elements: {
      bar: {
        borderRadius: { topLeft: barRadius, topRight: barRadius, bottomLeft: 0, bottomRight: 0 },
        borderSkipped: 'bottom',
      },
    },
    barPercentage: 0.8,
    categoryPercentage: 0.75,
  };

  return (
    <div className="bar-chart-container">
      <Chart ref={chartRef} type="bar" options={options} data={data as any} />
    </div>
  );
};
